import { NetworkSpeed } from '../types';
import { AudioAssetResolver } from './AudioAssetResolver';
import { MAX_PRELOAD_FAST, MAX_PRELOAD_MEDIUM, MAX_PRELOAD_SLOW } from '../constants/defaults';

interface CacheEntry {
  number: number;
  audio: HTMLAudioElement;
  timestamp: number;
}

export class AudioPreloader {
  private cache: Map<string, CacheEntry> = new Map();
  private maxCache: number = MAX_PRELOAD_FAST;
  private prefetchCount: number = MAX_PRELOAD_FAST;

  configure(speed: NetworkSpeed): void {
    switch (speed) {
      case 'fast':
        this.prefetchCount = MAX_PRELOAD_FAST;
        this.maxCache = MAX_PRELOAD_FAST;
        break;
      case 'medium':
        this.prefetchCount = MAX_PRELOAD_MEDIUM;
        this.maxCache = MAX_PRELOAD_MEDIUM;
        break;
      case 'slow':
        this.prefetchCount = MAX_PRELOAD_SLOW;
        this.maxCache = MAX_PRELOAD_SLOW;
        break;
      case 'offline':
        this.prefetchCount = 0;
        this.maxCache = 0;
        break;
    }

    // Detect low-memory devices
    const deviceMemory = (navigator as unknown as Record<string, number>).deviceMemory;
    if (deviceMemory && deviceMemory < 2) {
      this.maxCache = Math.min(this.maxCache, 1);
      this.prefetchCount = Math.min(this.prefetchCount, 1);
    }
  }

  preloadNext(
    queue: number[],
    resolver: AudioAssetResolver,
    lang: string,
    style: string
  ): void {
    const toPreload = queue.slice(0, this.prefetchCount);

    for (const num of toPreload) {
      const key = this.makeKey(lang, style, num);
      if (this.cache.has(key)) continue;

      // Evict if cache full (LRU)
      while (this.cache.size >= this.maxCache && this.maxCache > 0) {
        this.evictOldest();
      }

      if (this.maxCache === 0) return;

      const url = resolver.resolve(lang, style, num);
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = url;

      this.cache.set(key, {
        number: num,
        audio,
        timestamp: Date.now(),
      });
    }
  }

  get(lang: string, style: string, number: number): HTMLAudioElement | undefined {
    const key = this.makeKey(lang, style, number);
    const entry = this.cache.get(key);
    if (entry) {
      // Update timestamp for LRU
      entry.timestamp = Date.now();
      return entry.audio;
    }
    return undefined;
  }

  invalidateCache(): void {
    this.cache.clear();
  }

  getCacheSize(): number {
    return this.cache.size;
  }

  getEstimatedMemoryMB(): number {
    // Rough estimate: ~5MB per cached audio file
    return this.cache.size * 5;
  }

  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      const entry = this.cache.get(oldestKey);
      if (entry) {
        entry.audio.src = '';
      }
      this.cache.delete(oldestKey);
    }
  }

  private makeKey(lang: string, style: string, number: number): string {
    return `${lang}/${style}/${number}`;
  }
}
